import type { Bindings } from './bindings';

/** Keys that scroll the page or activate buttons; swallowed while a match has focus. */
const SCROLL_KEYS = new Set(['Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End']);

export interface KeyboardOptions {
  /** When false (menus, overlays), keys are left alone so buttons work normally. */
  isActive: () => boolean;
  onEscape?: () => void;
}

/**
 * Tracks held keys and fire presses. Fire is edge-triggered: a press is
 * reported once on the next sample, so holding the key never auto-fires.
 */
export class KeyboardController {
  private held = new Set<string>();
  private pressed = new Set<string>();
  private bound = new Set<string>();
  private fireKeys = new Set<string>();

  constructor(
    private bindings: Bindings,
    private readonly opts: KeyboardOptions,
  ) {
    this.setBindings(bindings);
  }

  setBindings(bindings: Bindings): void {
    this.bindings = bindings;
    this.bound = new Set([...Object.values(bindings[0]), ...Object.values(bindings[1])]);
    this.fireKeys = new Set([bindings[0].fire, bindings[1].fire]);
  }

  attach(): void {
    window.addEventListener('keydown', this.onKeyDown, { capture: true });
    window.addEventListener('keyup', this.onKeyUp, { capture: true });
    window.addEventListener('blur', this.clear);
  }

  detach(): void {
    window.removeEventListener('keydown', this.onKeyDown, { capture: true });
    window.removeEventListener('keyup', this.onKeyUp, { capture: true });
    window.removeEventListener('blur', this.clear);
    this.clear();
  }

  /** On-screen gamepad: hold a key exactly as if it were pressed on the keyboard. */
  virtualDown(code: string): void {
    if (this.held.has(code)) return;
    this.held.add(code);
    if (this.fireKeys.has(code)) this.pressed.add(code);
  }

  virtualUp(code: string): void {
    this.held.delete(code);
  }

  readonly clear = (): void => {
    this.held.clear();
    this.pressed.clear();
  };

  /** Per-player pad state. Consumes the fire press. */
  pad(player: 0 | 1): { up: boolean; down: boolean; left: boolean; right: boolean; action: boolean; actionPressed: boolean } {
    const b = this.bindings[player];
    const active = this.opts.isActive();
    const pressed = active && this.pressed.has(b.fire);
    this.pressed.delete(b.fire);
    if (!active) return { up: false, down: false, left: false, right: false, action: false, actionPressed: false };
    return {
      up: this.held.has(b.forward),
      down: this.held.has(b.back),
      left: this.held.has(b.left),
      right: this.held.has(b.right),
      action: this.held.has(b.fire),
      actionPressed: pressed,
    };
  }

  /** Solo play: either player's key set (WASD/Space or arrows/Enter) controls Player 1. */
  padAny(): { up: boolean; down: boolean; left: boolean; right: boolean; action: boolean; actionPressed: boolean } {
    const a = this.pad(0);
    const b = this.pad(1);
    return {
      up: a.up || b.up,
      down: a.down || b.down,
      left: a.left || b.left,
      right: a.right || b.right,
      action: a.action || b.action,
      actionPressed: a.actionPressed || b.actionPressed,
    };
  }

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.code === 'Escape' && this.opts.onEscape) {
      e.preventDefault();
      this.opts.onEscape();
      return;
    }
    if (!this.opts.isActive() || e.metaKey || e.ctrlKey) return;
    if (this.bound.has(e.code) || SCROLL_KEYS.has(e.code)) e.preventDefault();
    if (!this.bound.has(e.code) || e.repeat) return;
    this.held.add(e.code);
    if (this.fireKeys.has(e.code)) this.pressed.add(e.code);
  };

  private readonly onKeyUp = (e: KeyboardEvent): void => {
    this.held.delete(e.code);
    if (this.opts.isActive() && (this.bound.has(e.code) || SCROLL_KEYS.has(e.code))) e.preventDefault();
  };
}
